import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { countFormat } from './chart-utils'

export interface DonutSegment {
  key: string
  label: string
  value: number
  /** Tên biến màu RGB, ví dụ '--c-delivered'. */
  color: string
}

/**
 * Vòng tròn tỷ lệ + chú thích có số và phần trăm (chú thích luôn hiện, nên
 * màu không bao giờ là kênh nhận diện duy nhất). Giữa các cung có khe 2px
 * màu nền. Rê chuột vào cung hoặc dòng chú thích thì số ở giữa đổi theo.
 */
export function Donut({
  segments,
  unit = 'đơn',
  emptyLabel = 'Chưa có dữ liệu',
  compact,
  percent = true,
  className,
}: {
  segments: DonutSegment[]
  unit?: string
  emptyLabel?: string
  /** Điện thoại: vòng nhỏ đứng cạnh chú thích (bản thường xếp dọc dưới 640px) */
  compact?: boolean
  /** Cột phần trăm cạnh từng dòng chú thích (Tổng quan tắt — chỉ cần số đơn) */
  percent?: boolean
  className?: string
}) {
  const [active, setActive] = useState<string | null>(null)
  const total = segments.reduce((s, x) => s + x.value, 0)
  const current = segments.find((s) => s.key === active)
  const pctOf = (v: number) => (total > 0 ? `${((v / total) * 100).toFixed(1).replace('.', ',')}%` : '—')

  return (
    <div className={cn('flex items-center', compact ? 'gap-4' : 'flex-col gap-5 sm:flex-row sm:items-center', className)}>
      <DonutRing segments={segments} active={active} onActive={setActive} emptyLabel={emptyLabel} className={compact ? 'size-[104px]' : 'size-36'}>
        <div className={cn('font-bold leading-tight text-ink', compact ? 'text-lg' : 'text-xl')}>
          {total > 0 ? countFormat.format(current ? current.value : total) : '0'}
        </div>
        <div className="max-w-[88px] text-[11px] leading-tight text-muted">
          {total > 0 ? (current ? current.label : unit) : emptyLabel}
        </div>
      </DonutRing>

      <ul className={cn('grid w-full min-w-0', compact ? 'gap-0.5' : 'gap-2')}>
        {segments.map((s) => (
          <li
            key={s.key}
            className={cn(
              'grid items-center rounded-md py-0.5 motion-state',
              compact ? 'gap-2 px-1 text-[13px]' : 'gap-2.5 px-1.5 text-sm',
              !percent ? 'grid-cols-[auto_minmax(0,1fr)_auto]' : compact ? 'grid-cols-[auto_minmax(0,1fr)_auto_2.5rem]' : 'grid-cols-[auto_minmax(0,1fr)_auto_3.25rem]',
              active === s.key && 'bg-paperSoft',
            )}
            onPointerEnter={() => setActive(s.key)}
            onPointerLeave={() => setActive(null)}
          >
            <span className="size-2.5 rounded-[3px]" style={{ background: `rgb(var(${s.color}))` }} aria-hidden />
            <span className="truncate text-inkSoft">{s.label}</span>
            <span className="font-semibold tabular-nums text-ink">{countFormat.format(s.value)}</span>
            {percent && <span className="text-right tabular-nums text-muted">{pctOf(s.value)}</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Chỉ vòng tròn (chữ giữa vòng do nơi dùng truyền vào) — cho trang tự vẽ chú thích riêng. Cung đang chọn do nơi dùng
 * giữ (`active` / `onActive`) để chú thích và vòng sáng cùng nhau. Kích thước theo `className` (vd. size-40).
 */
export function DonutRing({
  segments,
  active,
  onActive,
  emptyLabel = 'Chưa có dữ liệu',
  className,
  children,
}: {
  segments: DonutSegment[]
  active?: string | null
  onActive?: (key: string | null) => void
  emptyLabel?: string
  className?: string
  /** Chữ giữa vòng */
  children?: ReactNode
}) {
  const total = segments.reduce((s, x) => s + x.value, 0)
  const R = 46
  const C = 2 * Math.PI * R
  const nonZero = segments.filter((s) => s.value > 0).length
  const GAP = nonZero > 1 ? 2.5 : 0

  let offset = 0
  const arcs = segments.map((s) => {
    const len = total > 0 ? (s.value / total) * C : 0
    const arc = { ...s, dash: Math.max(0, len - GAP), offset }
    offset += len
    return arc
  })

  return (
    <div className={cn('relative shrink-0', className)}>
      <svg viewBox="0 0 120 120" className="size-full -rotate-90" role="img"
        aria-label={total > 0 ? segments.map((s) => `${s.label} ${countFormat.format(s.value)}`).join(', ') : emptyLabel}>
        <circle cx="60" cy="60" r={R} fill="none" stroke="rgb(var(--paper-soft))" strokeWidth="14" />
        {arcs.map((a) =>
          a.dash > 0 ? (
            <circle
              key={a.key}
              cx="60"
              cy="60"
              r={R}
              fill="none"
              stroke={`rgb(var(${a.color}))`}
              strokeWidth={active === a.key ? 17 : 14}
              strokeDasharray={`${a.dash} ${C - a.dash}`}
              strokeDashoffset={-a.offset}
              className="cursor-default transition-[stroke-width] duration-150"
              onPointerEnter={() => onActive?.(a.key)}
              onPointerLeave={() => onActive?.(null)}
            />
          ) : null,
        )}
      </svg>
      <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
        <div>{children}</div>
      </div>
    </div>
  )
}
